import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};

/**
 * One implementation of single-conversation assignment (agent, team,
 * priority) for every entry point: side panel, command bar and context
 * menu. Each call updates the list at once, calls the API and reports
 * success or failure the same way wherever it was triggered.
 */
export function useConversationAssignment() {
  const store = useStore();
  const { t } = useI18n();

  const findChat = conversationId =>
    store.getters.getConversationById(conversationId);

  const priorityLabel = priority =>
    t(PRIORITY_LABEL_KEYS[priority] || 'CONVERSATION.PRIORITY.OPTIONS.NONE');

  // agent: an agent object, or null / id 0 to unassign. `silent` leaves the
  // success message to a caller that reports a larger action, and rethrows.
  const assignAgent = async (
    conversationId,
    agent,
    { silent = false } = {}
  ) => {
    const agentId = agent?.id || null;
    const previous = findChat(conversationId)?.meta?.assignee || null;
    store.dispatch('setCurrentChatAssignee', {
      conversationId,
      assignee: agentId ? agent : null,
    });
    try {
      await store.dispatch('assignAgent', { conversationId, agentId });
      if (!silent) useAlert(t('CONVERSATION.CHANGE_AGENT'));
    } catch (error) {
      store.dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: previous,
      });
      if (silent) throw error;
      useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
    }
  };

  // team: a team object, or null / id 0 to remove the team.
  const assignTeam = async (conversationId, team) => {
    const teamId = team?.id || 0;
    const previous = findChat(conversationId)?.meta?.team || null;
    store.dispatch('setCurrentChatTeam', {
      conversationId,
      team: teamId ? team : null,
    });
    try {
      await store.dispatch('assignTeam', { conversationId, teamId });
      useAlert(t('CONVERSATION.CHANGE_TEAM'));
    } catch (error) {
      store.dispatch('setCurrentChatTeam', { conversationId, team: previous });
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
    }
  };

  const assignPriority = async (conversationId, priority, { from } = {}) => {
    const oldValue = findChat(conversationId)?.priority ?? null;
    store.dispatch('setCurrentChatPriority', { conversationId, priority });
    try {
      await store.dispatch('assignPriority', { conversationId, priority });
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        oldValue,
        newValue: priority,
        from,
      });
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          priority: priorityLabel(priority),
          conversationId,
        })
      );
    } catch (error) {
      store.dispatch('setCurrentChatPriority', {
        conversationId,
        priority: oldValue,
      });
      useAlert(t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'));
    }
  };

  return { assignAgent, assignTeam, assignPriority };
}
